<script>
import { mapState } from 'vuex';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import ServiceProviderModel from '@models/ServiceProvider';
import isFunction from 'lodash/isFunction';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';

export default {
  components: {
    BlockingContainer,
    quillEditor,
  },
  mixins: [editFormChange],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
  },
  data() {
    return {
      serviceProvider: {
        name: null,
        contact: null,
        email: null,
        street: null,
        postal_code: null,
        town: null,
        country: null,
        notes: null,
        phone_number: null,
      },
    };
  },
  computed: {
    ...mapState({
      quillOptions: (state) => state.common.quillOptions,
    }),
  },
  mounted() {
    if (this.loadedId) {
      this.$wait.start('service-provider loading');
      ServiceProviderModel.find(this.loadedId).then((response) => {
        this.serviceProvider = Object.assign({}, this.serviceProvider, response.service_provider);
        this.$wait.end('service-provider loading');
        this.$nextTick(() => {
          this.initChangeWatcher(['serviceProvider'], this.validate);
        });
      });
    }
  },
  methods: {
    validate(callback) {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$wait.start('service-provider loading');
          this.submit(callback);
        }
      });
    },
    submit(callback) {
      if (this.$route.params.id) {
        this.serviceProvider.id = this.$route.params.id;
      }

      new ServiceProviderModel(this.serviceProvider)
        .save()
        .then((response) => {
          this.$emit('submitted', callback && isFunction(callback));
          if (callback && isFunction(callback)) callback();
          this.$wait.end('service-provider loading');
        })
        .catch(() => this.$wait.end('service-provider loading'));
    },
  },
};
</script>

<template>
  <div>
    <BlockingContainer v-if="$wait.is('service-provider loading')"></BlockingContainer>
    <b-form @submit.prevent="validate">
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.company_name') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="name"
          v-model="serviceProvider.name"
          v-validate="'required|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="name"
          :data-vv-as="$t('fields.name')"
          type="text"
          :state="errors.has('name') ? false : null"
          aria-describedby="input-feedback-name"
          :placeholder="$t('placeholders.company_name')"
        >
        </b-form-input>
        <b-form-invalid-feedback id="input-feedback-name">
          <template v-show="errors.has('name')">{{ errors.first('name') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group>
        <template v-slot:label class="col-form-legend col-12">
          {{ $t('fields.email') }}:
          <span class="status-star text-danger">*</span>
        </template>
        <b-form-input
          id="email"
          v-model="serviceProvider.email"
          v-validate="'required|email|max:255'"
          data-vv-value-path="localValue"
          data-vv-name="email"
          :data-vv-as="$t('fields.email')"
          type="text"
          :state="errors.has('email') ? false : null"
          aria-describedby="input-feedback-email"
          :placeholder="$t('placeholders.email')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-email">
          <template v-show="errors.has('email')">{{ errors.first('email') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.contact')}:`">
        <b-form-input
          id="contact"
          v-model="serviceProvider.contact"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="contact"
          :data-vv-as="$t('fields.contact')"
          type="text"
          :state="errors.has('contact') ? false : null"
          aria-describedby="input-feedback-contact"
          :placeholder="$t('placeholders.contact_name')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-contact">
          <template v-show="errors.has('contact')">{{ errors.first('contact') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.phone_number')}:`">
        <b-form-input
          v-model="serviceProvider.phone_number"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="phone_number"
          :data-vv-as="$t('fields.phone_number')"
          type="text"
          :state="errors.has('phone_number') ? false : null"
          aria-describedby="input-feedback-phone_number"
          :placeholder="$t('placeholders.phone_number')"
        >
        </b-form-input>
        <b-form-invalid-feedback id="input-feedback-phone_number">
          <template v-show="errors.has('phone_number')">{{ errors.first('phone_number') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.street')}:`">
        <b-form-input
          id="street"
          v-model="serviceProvider.street"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="street"
          :data-vv-as="$t('fields.street')"
          type="text"
          :state="errors.has('street') ? false : null"
          aria-describedby="input-feedback-street"
          :placeholder="$t('placeholders.street')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-street">
          <template v-show="errors.has('street')">{{ errors.first('street') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.postal_code')}:`">
        <b-form-input
          id="postal_code"
          v-model="serviceProvider.postal_code"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="postal_code"
          :data-vv-as="$t('fields.postal_code')"
          type="text"
          :state="errors.has('postal_code') ? false : null"
          aria-describedby="input-feedback-postal_code"
          :placeholder="$t('placeholders.postal_code')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-postal_code">
          <template v-show="errors.has('postal_code')">{{ errors.first('postal_code') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.town')}:`">
        <b-form-input
          id="town"
          v-model="serviceProvider.town"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="town"
          :data-vv-as="$t('fields.town')"
          type="text"
          :state="errors.has('town') ? false : null"
          aria-describedby="input-feedback-town"
          :placeholder="$t('placeholders.town')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-town">
          <template v-show="errors.has('town')">{{ errors.first('town') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.country')}:`">
        <b-form-input
          id="country"
          v-model="serviceProvider.country"
          v-validate="'max:255'"
          data-vv-value-path="localValue"
          data-vv-name="country"
          :data-vv-as="$t('fields.country')"
          type="text"
          :state="errors.has('country') ? false : null"
          aria-describedby="input-feedback-country"
          :placeholder="$t('placeholders.country')"
        >
        </b-form-input>

        <b-form-invalid-feedback id="input-feedback-country">
          <template v-show="errors.has('country')">{{ errors.first('country') }}</template>
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group :label="`${$t('fields.notes')}:`">
        <quill-editor ref="notes" v-model="serviceProvider.notes" :options="quillOptions" />
      </b-form-group>
      <b-button type="submit" :disabled="errors.any()" :variant="'outline-' + (!errors.any() ? 'dark' : 'danger')">
        {{ $t('actions.submit') }}
      </b-button>
    </b-form>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.avatarImg {
  height: 160px;
  &:hover {
    cursor: pointer;
    border: 1px solid map_get($theme-colors, 'dark');
  }
}

.clearButtonContainer {
  position: absolute;
  top: 5px;
  right: 5px;
  padding: 0 !important;
}
</style>
